import { useMemo } from "react";
import { PackageDepartmentEnum } from "@/__generated__/graphql";
import { formatPrice, singaporeTaxCalculation } from "@/lib/helpers/number";
import { getTitleCase } from "@/lib/utils";
import type { ServiceName } from "@/types/service";

import { AvatarTimeSlotCard } from "./avatar-time-slot-card";
import { NormalTimeSlotCard } from "./normal-time-slot-card";
import { SimpleTimeSlotCard } from "./simple-time-slot-card";

export const TimeSlotCard = ({
  department,
  onCardClick,
  onClickWorkerPortfolio,
  startTime,
  endTime,
  rateValue,
  discount,
  isCheaper,
  workerName,
  workerRating,
  avatarUrl,
  active,
  hidePricing,
}: {
  active?: boolean;
  department: PackageDepartmentEnum;
  onCardClick(): void;
  onClickWorkerPortfolio(): void;
  date: string; // unused?
  startTime: string;
  endTime: string;
  rateValue?: number;
  discount?: number;
  isCheaper?: boolean;
  selectedWorkerName?: string | null; // unused?
  workerName: string;
  workerRating: number;
  avatarUrl: string;
  name: ServiceName; // unused?
  hidePricing?: boolean;
}) => {
  const isTimeSlotWithPortfolio =
    department === PackageDepartmentEnum.HomeBeauty ||
    department === PackageDepartmentEnum.ElderCare ||
    department === PackageDepartmentEnum.Babysitter;

  const formatName = (name: string): string => {
    return getTitleCase(name).slice(0, 15) + (name.length > 15 ? ".." : "");
  };
  const formattedWorkerName = useMemo(() => {
    return formatName(workerName);
  }, [workerName]);

  const priceWithTax = useMemo(() => {
    if (discount) {
      return `(${formatPrice(singaporeTaxCalculation(discount).price)} w/GST)`;
    }

    if (rateValue) {
      return `(${formatPrice(singaporeTaxCalculation(rateValue ?? 0).price)} w/GST)`;
    }
    return "";
  }, [discount, rateValue]);

  const formattedTime = (time: string) => {
    return time.split(":").slice(0, 2).join(":");
  };

  const timeRange = useMemo(() => {
    return `${formattedTime(startTime)} - ${formattedTime(endTime)}`;
  }, [startTime, endTime]);

  if (isTimeSlotWithPortfolio)
    return (
      <AvatarTimeSlotCard
        active={active}
        onCardClick={onCardClick}
        timeRange={timeRange}
        workerName={formattedWorkerName}
        workerRating={workerRating}
        avatarUrl={avatarUrl}
        onClickProfolio={onClickWorkerPortfolio}
        department={department}
      />
    );

  if (
    [
      PackageDepartmentEnum.Aircon,
      PackageDepartmentEnum.CarpetUpholstery,
      PackageDepartmentEnum.DeepCleaning,
      PackageDepartmentEnum.Massage,
      PackageDepartmentEnum.PetCare,
    ].includes(department)
  )
    return (
      <SimpleTimeSlotCard
        timeRange={timeRange}
        active={active}
        onCardClick={onCardClick}
      />
    );

  return (
    <NormalTimeSlotCard
      timeRange={timeRange}
      rateValue={rateValue}
      active={active}
      priceWithTax={priceWithTax}
      onCardClick={onCardClick}
      isCheaper={isCheaper}
      hidePricing={hidePricing}
    />
  );
};
